import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { requestAddClientVariable } from "../../../actions/clientVariableActions";
import FormGroupWithError from "../../misc/FormGroupWithError";

import { getAllClientVariableNames } from "../../../reducers/clientVariables";
import Checkbox from "@material-ui/core/Checkbox";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import { ColorButton } from "../../material-components/Misc/Button";
import { IAppState } from "../../../types/stores/index";
import { ClientVariableKind, IVariableType, IVariableGrouping } from "../../../types/stores/vars";

const clientVariableKindMap: {
    [key: string]: ClientVariableKind;
} = { string: "string", date: "date", field: "field", stringField: "stringField" };

const theme = createTheme({
    overrides: {
        MuiInput: {
            underline: {
                "&&&&:hover:after": {
                    borderBottom: "2px solid #406eb3",
                },
                "&&&&:after": {
                    borderBottom: "2px solid #406eb3",
                },
            },
        },
    },
});
const stringVal: IVariableType = { SubTypeId: 0, TypeId: 0, TypeName: "string" };
const fieldVal: IVariableType = { SubTypeId: 0, TypeId: 0, TypeName: "field" };

type Props = {
    scope: string;
    isShowDestination: boolean;
    manageDestVar: boolean;
};

const ClientVariableAdder: React.FC<Props> = ({ scope, isShowDestination, manageDestVar }: Props) => {
    const dispatch = useDispatch();
    const [name, setName] = useState("");
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures || []);
    const [variableType, setVariableType] = useState<IVariableType | null>(null);
    const [variableGroup, setVariableGrouping] = useState<IVariableGrouping | null>(null);
    const [type, setType] = useState("");
    const [isDestination, setIsDestination] = useState(false);
    const [errors, setErrors] = useState<{ name: string; type: string }>({ name: "", type: "" });
    const [requestPending, setRequestPending] = useState(false);

    const variableTypes = useSelector((state: IAppState) => state.vars.variableTypes || []);
    const variableGroups = useSelector((state: IAppState) => state.vars.variableGroupings || []);
    const clientVariableNames = useSelector((state: IAppState) => getAllClientVariableNames(state));
    const keywordNames: Array<string> = ["input quantity", "matched quantity", "final quantity"];
    const entityName = scope == "OfferCode" ? "offer code" : "variable";

    useEffect(() => {
        manageDestinationVars(manageDestVar);
    }, [manageDestVar]);

    const handleChangeName = (name: string) => setName(name);

    const handleChangeTypeJson = typeName => {
        let jsonVariables: IVariableType = JSON.parse(typeName);
        let value: IVariableType | null = null;
        value = jsonVariables;

        if (value && clientVariableKindMap[value.TypeName]) {
            setType(clientVariableKindMap[value!.TypeName]);
            setVariableType(value);
        }
    };

    const handleChangeType = (typeName: string) => {
        let value: IVariableType | null = null;
        value = variableTypes.find(x => (x!.TypeName = typeName)) || null;

        if (value && clientVariableKindMap[value.TypeName]) {
            setType(clientVariableKindMap[value!.TypeName]);
            setVariableType(value);
        }
    };

    const handleChangeGrouping = (groupingId: number) => {
        let value: IVariableGrouping | null = null;
        value = variableGroups.find(x => x!.Id == groupingId) || null;

        if (value) {
            setVariableGrouping(value);
        }
    };

    const handleChangeIsDestination = (val: boolean) => {
        // if (val) { // #6191 Reverted. Need to disable field selection.
        //     // #6191 Allow for Offer Code type of "field" to be available for Destination Offer codes
        // handleChangeType(type); // No need no matter #6191 revert or unrevert
        // // Reverted. Need to disable field selection.
        // setType("string");
        // setVariableType(stringVal);
        // }
        setType("");
        setVariableType(null);
        setIsDestination(val);
    };

    const validate = () => {
        const errorsList = { name: "", type: "" };
        if (!name) {
            errorsList.name = "Please provide a " + entityName + " name.";
        } else if (clientVariableNames.includes(name)) {
            errorsList.name = "This name is already used. Variables and Offer Codes cannot have the same name.";
        } else if (keywordNames.includes(name.toLowerCase())) {
            errorsList.name = "This name is disallowed because it is the reserved column name headers.";
        }

        if (!type || !(variableType && variableType!.SubTypeId != null)) {
            errorsList.type = "Please provide a type.";
        }
        setErrors(errorsList);
        return Boolean(errorsList.name == "" && errorsList.type == "");
    };

    const save = () => {
        if (!validate()) {
            return;
        }

        const onAddCallback = () => {
            setRequestPending(false);
            resetValues();
        };
        const onErrorCallback = () => setRequestPending(false);
        setRequestPending(true);

        let saveScope = scope;
        if (isDestination) {
            saveScope = scope == "OfferCode" ? "DestinationOffer" : "DestinationVariable";
        }
        dispatch(
            requestAddClientVariable(
                name,
                type as ClientVariableKind,
                saveScope,
                variableType!.SubTypeId,
                variableGroup != null && variableType != null && variableType.TypeName == stringVal.TypeName
                    ? variableGroup!.Id
                    : null,
                onAddCallback,
                onErrorCallback
            )
        );
    };

    const resetValues = () => {
        if (manageDestVar) {
            setName("");
            setVariableGrouping(null);
        } else {
            setName("");
            setType("string");
            setVariableType(stringVal);
            setIsDestination(false);
            setVariableGrouping(null);
        }
    };

    const manageDestinationVars = value => {
        let typeValue = value ? "string" : "";
        let variableValue = value ? stringVal : null;
        setIsDestination(value);
        setType(typeValue);
        setVariableType(variableValue);
    };
    const renderValue = value => <div>{value}</div>;

    return (
        <div>
            <div style={{ display: "flex", width: "100%" }}>
                <ThemeProvider theme={theme}>
                    <FormGroupWithError errorText={errors.name}>
                        <FormControl style={{ minWidth: "15vw", paddingRight: "10px" }}>
                            <TextField
                                inputProps={{ style: { fontSize: "15px" }, maxLength: 128 }}
                                label={
                                    entityName
                                        .split(" ")
                                        .map(s => s.charAt(0).toUpperCase() + s.substring(1))
                                        .join(" ") + " Name"
                                }
                                disabled={requestPending}
                                type="text"
                                value={name}
                                onChange={e => handleChangeName(e.target.value)}
                            />
                        </FormControl>
                    </FormGroupWithError>
                    <FormGroupWithError errorText={errors.type}>
                        <FormControl style={{ minWidth: "15vw", paddingRight: "10px" }}>
                            <InputLabel>
                                {entityName
                                    .split(" ")
                                    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
                                    .join(" ")}{" "}
                                Type
                            </InputLabel>

                            {isDestination == false ? (
                                <Select
                                    style={{ fontSize: "14px" }}
                                    value={variableType ? variableType : ""}
                                    onChange={e => handleChangeTypeJson(e.target.value)}
                                    disabled={requestPending}
                                    displayEmpty
                                    renderValue={() => renderValue(variableType?.Description)}
                                >
                                    {variableTypes.map((x, i) => (
                                        <MenuItem
                                            key={i + "_" + x.TypeId + "_" + x.SubTypeId}
                                            value={JSON.stringify({
                                                CompanyId: x.CompanyId,
                                                Description: x.Description,
                                                DropdownValues: x.DropdownValues,
                                                IsActive: x.IsActive,
                                                Regex: x.Regex,
                                                SubTypeId: x.SubTypeId,
                                                TypeId: x.TypeId,
                                                TypeName: x.TypeName,
                                            })}
                                        >
                                            {x.Description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) : (
                                <Select
                                    style={{ fontSize: "14px" }}
                                    value={variableType ? variableType.TypeName : ""}
                                    onChange={e => handleChangeType(e.target.value as string)}
                                    disabled={requestPending}
                                    displayEmpty
                                >
                                    <MenuItem value={stringVal.TypeName}>String</MenuItem>
                                    <MenuItem value={fieldVal.TypeName}>Field</MenuItem>
                                </Select>
                            )}
                        </FormControl>
                    </FormGroupWithError>
                    {enabledFeatures.includes("flow-tool-multi-export") &&
                        (variableType == null || variableType.TypeName == stringVal.TypeName) && (
                            <FormGroupWithError>
                                <FormControl style={{ minWidth: "15vw", paddingRight: "10px" }}>
                                    <InputLabel>
                                        {entityName
                                            .split(" ")
                                            .map(s => s.charAt(0).toUpperCase() + s.substring(1))
                                            .join(" ")}{" "}
                                        Grouping
                                    </InputLabel>
                                    <Select
                                        style={{ fontSize: "14px" }}
                                        value={variableGroup ? variableGroup : ""}
                                        onChange={e => handleChangeGrouping(Number(e.target.value))}
                                        disabled={requestPending}
                                        displayEmpty
                                        renderValue={() => renderValue(variableGroup?.GroupName)}
                                    >
                                        {variableGroups.map((x, i) => (
                                            <MenuItem key={i + "_" + x.Id + "_" + x.GroupName} value={x.Id}>
                                                {x.GroupName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </FormGroupWithError>
                        )}
                </ThemeProvider>
            </div>
            <div style={{ display: "flex", height: "60px", width: "100%" }}>
                {isShowDestination && entityName !== "variable" && (
                    <div>
                        <div>
                            <Checkbox
                                id="destination"
                                name="isDestination"
                                onClick={() => handleChangeIsDestination(!isDestination)}
                                color="primary"
                                checked={isDestination}
                                disabled={requestPending}
                            />
                            <label className="control-label" htmlFor="">
                                Destination
                            </label>
                        </div>
                    </div>
                )}
                <div style={{ marginLeft: "auto", paddingTop: "27px" }} className="text-right">
                    <ColorButton
                        style={{ fontSize: "14px", fontWeight: "normal" }}
                        onClick={() => save()}
                        disabled={requestPending}
                        backgroundcolor="#406eb3"
                    >
                        <span style={{ padding: "0px 1px" }}>
                            Add{" "}
                            {entityName
                                .split(" ")
                                .map(s => s.charAt(0).toUpperCase() + s.substring(1))
                                .join(" ")}
                        </span>
                    </ColorButton>
                </div>
            </div>
        </div>
    );
};

export default ClientVariableAdder;
